import { formatPersonName } from "./people-display";

const MAILBOX_ROLES = new Set([
  "info",
  "admin",
  "hello",
  "contact",
  "support",
  "sales",
  "office",
  "team",
  "noreply",
  "no-reply",
  "mail",
  "enquiries",
  "inquiries",
]);

const NAME_KEYS = new Set(["name", "ownername"]);
const ORG_KEYS = new Set(["displayname", "company", "companyname"]);

function compactKey(fieldKey: string): string {
  return fieldKey.replaceAll("_", "").replaceAll("-", "").toLowerCase();
}

/** Title-case a stored name when the field is a person or organization name. */
export function presentStoredName(fieldKey: string, value: string): string {
  const key = compactKey(fieldKey);
  if (!NAME_KEYS.has(key) && !ORG_KEYS.has(key)) return value.trim();
  return formatPersonName(value);
}

/**
 * Form and OCR garbage such as "J j is ffln m". A single real word like
 * "Blanc" is kept so a broker-entered name is not overwritten.
 */
export function isGarbagePersonName(value: string | null | undefined): boolean {
  const name = value?.trim() ?? "";
  if (name.length < 2) return true;
  const tokens = name.split(/\s+/).filter(Boolean);
  const lettersOf = (token: string) => token.replace(/[^A-Za-z]/g, "");
  const letterTokens = tokens.map(lettersOf).filter((token) => token.length > 0);
  if (letterTokens.length === 0) return true;
  const singleLetters = letterTokens.filter((token) => token.length === 1).length;
  if (singleLetters >= 2) return true;
  return letterTokens.some((token) => {
    if (token.length < 4) return false;
    const vowels = token.match(/[aeiouy]/gi)?.length ?? 0;
    return vowels / token.length < 0.2;
  });
}

function looksLikeNamePart(part: string): boolean {
  if (part.length < 2 || part.length > 14) return false;
  if (!/^[a-z]+$/.test(part)) return false;
  const vowels = part.match(/[aeiouy]/g)?.length ?? 0;
  return vowels / part.length >= 0.2;
}

function partScore(part: string): number {
  if (part.length < 3 || part.length > 12) return -1;
  if (/[^aeiouy]{4,}/.test(part)) return -1;
  const vowels = part.match(/[aeiouy]/g)?.length ?? 0;
  const ratio = vowels / part.length;
  if (ratio < 0.2 || ratio > 0.7) return -1;
  let score = 1;
  if (part.length >= 4 && part.length <= 8) score += 0.4;
  if (ratio >= 0.28 && ratio <= 0.55) score += 0.3;
  return score;
}

function splitConcatenatedName(token: string): [string, string] | null {
  if (token.length < 6 || token.length > 18 || !/^[a-z]+$/.test(token)) return null;
  const candidates: Array<{ left: string; right: string; score: number }> = [];
  for (let index = 3; index <= token.length - 3; index += 1) {
    const left = token.slice(0, index);
    const right = token.slice(index);
    const leftScore = partScore(left);
    const rightScore = partScore(right);
    if (leftScore < 0 || rightScore < 0) continue;
    const lengthBonus = index >= 4 && index <= 7 ? 0.5 : 0;
    const boundary =
      /[^aeiouy]$/.test(left) && /^[aeiouy]/.test(right) ? 0.45 : 0;
    const cluster = /^[^aeiouy]{2}/.test(right) ? -0.6 : 0;
    candidates.push({
      left,
      right,
      score: leftScore + rightScore + lengthBonus + boundary + cluster,
    });
  }
  candidates.sort((left, right) => right.score - left.score);
  const best = candidates[0];
  const second = candidates[1];
  if (!best || best.score < 2.2) return null;
  if (second && best.score - second.score < 0.35) return null;
  return [best.left, best.right];
}

/**
 * A mailbox that is clearly a person. "jasenedmond25@gmail.com" is Jasen Edmond.
 * Role mailboxes and ambiguous local parts return null.
 */
export function nameFromEmail(email: string | null | undefined): string | null {
  const local = email?.split("@")[0]?.split("+")[0]?.trim().toLowerCase() ?? "";
  if (!local || MAILBOX_ROLES.has(local)) return null;
  const stripped = local
    .replace(/\d+/g, " ")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const parts = stripped.split(" ").filter((part) => /[a-z]/.test(part));
  if (parts.some((part) => !looksLikeNamePart(part))) return null;
  if (parts.length >= 2 && parts.length <= 3) {
    return formatPersonName(parts.join(" "));
  }
  if (parts.length !== 1) return null;
  const split = splitConcatenatedName(parts[0] ?? "");
  if (!split) return null;
  return formatPersonName(`${split[0]} ${split[1]}`);
}
